import { useCallback, useEffect, useMemo, useState } from "react";

import { Practitioner, Reference } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { usePracticeId } from "@/hooks/usePracticeId";
import { joinReferenceString } from "@/utils/medplum";
import { showSystemErrorNotification, toast } from "@/utils/notifications";

import {
  fetchStatementSettingsQuestionnaire,
  fetchStatementSettingsResponse,
  saveStatementSettingsResponse,
} from "../services/statement-settings";
import {
  emptyStatementSettings,
  getStatementSettingsFields,
  parseStatementSettingsResponse,
  StatementSettingsField,
} from "../utils/statement-settings";
import { StatementSettingsFormType } from "../zod-schemas/statement-settings";

/** Loads and saves the practice-scoped statement settings. */
export function useStatementSettings(refreshKey = 0) {
  const medplum = useMedplum();
  const practiceId = usePracticeId();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [settings, setSettings] = useState<StatementSettingsFormType>(emptyStatementSettings);
  const [fields, setFields] = useState<StatementSettingsField[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const practitionerReference = useMemo<Reference<Practitioner> | undefined>(() => {
    if (!profile?.id) return undefined;
    return { reference: joinReferenceString("Practitioner", profile.id) };
  }, [profile]);

  const organizationReference = useMemo(
    () => (practiceId ? joinReferenceString("Organization", practiceId) : undefined),
    [practiceId],
  );

  const loadSettings = useCallback(
    async (options?: { showLoading?: boolean }) => {
      const showLoading = options?.showLoading ?? true;

      if (!organizationReference) {
        setSettings(emptyStatementSettings());
        setFields([]);
        setIsLoading(false);
        return;
      }

      try {
        if (showLoading) setIsLoading(true);
        medplum.invalidateSearches("QuestionnaireResponse");

        const questionnaire = await fetchStatementSettingsQuestionnaire(medplum);
        setFields(getStatementSettingsFields(questionnaire));
        if (!questionnaire) {
          setSettings(emptyStatementSettings());
          return;
        }

        const questionnaireResponse = await fetchStatementSettingsResponse(
          medplum,
          questionnaire,
          organizationReference,
        );
        setSettings(parseStatementSettingsResponse(questionnaireResponse));
      } catch (error) {
        setSettings(emptyStatementSettings());
        setFields([]);
        throw new Error("Error loading statement settings: " + error);
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [medplum, organizationReference],
  );

  useEffect(() => {
    loadSettings().catch(() => {
      showSystemErrorNotification();
    });
  }, [loadSettings, refreshKey]);

  /** Resolves true when the settings were written, false when the save could not be attempted. */
  const saveSettings = useCallback(
    async (values: StatementSettingsFormType): Promise<boolean> => {
      if (!practitionerReference || !practiceId) {
        toast({
          type: "error",
          title: "Cannot save statement settings",
          message: practitionerReference
            ? "Practice context is missing. Open Billing from a practice settings route (URL should include the practice id)."
            : "Your practitioner profile is not loaded. Refresh the page or sign in again.",
          autoClose: false,
        });
        return false;
      }

      try {
        const questionnaire = await fetchStatementSettingsQuestionnaire(medplum);
        if (!questionnaire) {
          throw new Error("Statement settings questionnaire not found");
        }

        medplum.invalidateSearches("QuestionnaireResponse");
        const existing = await fetchStatementSettingsResponse(
          medplum,
          questionnaire,
          joinReferenceString("Organization", practiceId),
        );

        await saveStatementSettingsResponse(
          medplum,
          questionnaire,
          values,
          practitionerReference,
          practiceId,
          existing,
        );

        await loadSettings({ showLoading: false });
        return true;
      } catch (error) {
        showSystemErrorNotification();
        console.error("Error saving statement settings:", error);
        throw error;
      }
    },
    [medplum, practiceId, practitionerReference, loadSettings],
  );

  return { settings, fields, isLoading, loadSettings, saveSettings };
}
